import EmblaCarousel from 'embla-carousel';
import { Fancybox } from '@fancyapps/ui';
import { FocusTrap, reverseAriaExpanded } from './utils';
import Player from '@vimeo/player';


// Hero slider

const heroSlider = document.querySelector('.hero-slider-outer');
const heroSlides = heroSlider?.querySelectorAll('.hero-slide');

if (heroSlider && heroSlides.length > 1) {
    const embla = EmblaCarousel(heroSlider, {
        loop: true,
        dragFree: true,
    });

    setInterval(embla.scrollNext, 5000);
}


// Hero video

const heroes = document.querySelectorAll('.hero, .landingpage-hero');

heroes.forEach(hero => {
    const container = hero.dataset.video ? hero : hero.querySelector('[data-video]');

    if (!container) {
        return;
    }

    const videoUrl = container.dataset.video;
    const autoplay = container.dataset.autoplay === 'true';
    const button = container.closest('section').querySelector('.hero-play');

    const player = new Player(container, {
        url: videoUrl,
        background: autoplay,
        dnt: true,
        controls: false,
        title: false,
        byline: false,
        portrait: false,
        loop: true,
    });

    player.on('loaded', () => {
        resizeCoverVideos(hero);
    });

    button?.addEventListener('click', () => {
        button.style.display = 'none';
        player.play();
    });
});


// Cover videos, styling them with CSS is not enough

window.resizeCoverVideos = function(context) {
    const coverVideos = (context ?? document).querySelectorAll('.cover-video iframe');

    coverVideos.forEach(iframe => {
        const videoWidth = parseFloat(iframe.getAttribute('width'));
        const videoHeight = parseFloat(iframe.getAttribute('height'));
        const videoAspectRatio = videoWidth / videoHeight;

        const container = iframe.parentElement;
        const containerRect = container.getBoundingClientRect();
        const containerAspectRatio = containerRect.width / containerRect.height;

        if (videoAspectRatio > containerAspectRatio) {
            iframe.style.width = containerRect.height * videoAspectRatio + 'px';
            iframe.style.height = containerRect.height + 'px';
        } else {
            iframe.style.height = containerRect.width / videoAspectRatio + 'px';
            iframe.style.width = containerRect.width + 'px';
        }

        iframe.classList.add('resized');
    });
}

window.addEventListener('resize', () => resizeCoverVideos());
resizeCoverVideos();


// Gallery slider

const galleryBlocks = document.querySelectorAll('.gallery');

galleryBlocks.forEach(block => { 
    const container = block.querySelector('.gallery-slider-outer');
    const slides = block.querySelectorAll('.gallery-slide-outer');
    const buttons = block.querySelectorAll('.gallery-nav-btn');

    if (slides.length > 1) {
        const embla = EmblaCarousel(container, { align: 'start', loop: true });

        buttons.forEach((button, i) => {
            button.addEventListener('click', () => embla.scrollTo(i));
        });

        embla.on('select', () => {
            const index = embla.selectedScrollSnap();
            buttons.forEach((button, i) => button.classList.toggle('active', index === i));
        });
    }
});


// Content sliders

export function initContentSliders(context = document) {
    const contentSliders = context.querySelectorAll('.content-slider-outer');

    contentSliders.forEach(container => {
        const embla = EmblaCarousel(container, { align: 'start'});
        const section = container.closest('section');
        
        const nav = section.querySelector('.slider-nav');
        const prevButton = section.querySelector('.slider-nav-prev');
        const nextButton = section.querySelector('.slider-nav-next');
    
        if (nav) {
            prevButton.addEventListener('click', embla.scrollPrev);
            nextButton.addEventListener('click', embla.scrollNext);
        
            embla.on('select', () => {
                prevButton.disabled = !embla.canScrollPrev();
                nextButton.disabled = !embla.canScrollNext();
            });
        
            embla.on('slidesInView', () => {
                nav.classList.toggle('hidden', !embla.canScrollNext() && !embla.canScrollPrev());
            });
        }
    });
}

initContentSliders();


// Logos slider

const logosSliders = document.querySelectorAll('.logos-slider-outer');

logosSliders.forEach(container => {
    const embla = EmblaCarousel(container, { align: 'start' });
    const section = container.closest('section');
    
    const prevButton = section.querySelector('.slider-nav-prev');
    const nextButton = section.querySelector('.slider-nav-next');

    prevButton.addEventListener('click', embla.scrollPrev);
    nextButton.addEventListener('click', embla.scrollNext);

    embla.on('select', () => {
        prevButton.disabled = !embla.canScrollPrev();
        nextButton.disabled = !embla.canScrollNext();
    });
});


// User scroll class

const headerWasWhite = document.body.classList.contains('header-white');
let lastScrollTop = 0;

function toggleHeaderScrollClass() {
    const currentScrollTop = window.scrollY || document.documentElement.scrollTop;
    const hasScrolledDown = currentScrollTop > 0 && currentScrollTop > lastScrollTop;

    lastScrollTop = currentScrollTop;

    document.body.classList.toggle('header-white', currentScrollTop > 0 || headerWasWhite);
    document.body.classList.toggle('user-has-scrolled-down', hasScrolledDown);
    document.body.classList.toggle('user-has-scrolled-up', !hasScrolledDown);
}

window.addEventListener('scroll', toggleHeaderScrollClass);
toggleHeaderScrollClass();


// Header language switcher

const headerLanguagesToggle = document.querySelector('.header-languages-toggle');
let headerLanguagesAreOpen = false;

function applyHeaderLanguageTabIndexes() {
    document.querySelectorAll('.header-languages-list a').forEach(link => {
        link.setAttribute('tabindex', headerLanguagesAreOpen ? '0' : '-1');
    });
}

headerLanguagesToggle?.addEventListener('click', () => {
    document.body.classList.toggle('languages-are-open');
    reverseAriaExpanded(headerLanguagesToggle);

    headerLanguagesAreOpen = !headerLanguagesAreOpen;
    applyHeaderLanguageTabIndexes();
});

applyHeaderLanguageTabIndexes();


// Header menu

const headerMenuMobile = document.querySelector('.header-menu-mobile');
const headerMenuToggle = document.querySelector('.header-menu-toggle');
const smallmenuOverlay = document.querySelector('.smallmenu-overlay');

let headerMenuIsOpen = false;
let headerMenuFocusTrap = null;

function openHeaderMenu() {
    document.body.classList.add('menu-is-open');
    headerMenuToggle.setAttribute('aria-expanded', 'true');
    headerMenuIsOpen = true;

    const focusableEls = [headerMenuToggle, ...headerMenuMobile.querySelectorAll('.smallmenu-topmenu > li > a, .smallmenu-topmenu > li > button')];
    headerMenuToggle.focus();

    headerMenuFocusTrap = new FocusTrap(headerMenuMobile, focusableEls);
    headerMenuFocusTrap.enable();
}

function closeHeaderMenu() {
    document.body.classList.remove('menu-is-open');
    headerMenuToggle.setAttribute('aria-expanded', 'false');
    headerMenuIsOpen = false;

    headerMenuToggle.focus();

    headerMenuFocusTrap?.disable();
}

function toggleHeaderMenu() {
    headerMenuIsOpen ? closeHeaderMenu() : openHeaderMenu();
}

headerMenuToggle.addEventListener('click', toggleHeaderMenu);
smallmenuOverlay.addEventListener('click', closeHeaderMenu);


// Small menu submenus

const smallmenuInner = document.querySelector('.smallmenu-inner');
const smallmenuSubmenus = document.querySelectorAll('.smallmenu-submenu');
const smallmenuSubmenuToggles = document.querySelectorAll('.smallmenu-submenu-toggle');
const smallMenuSubmenuClosers = document.querySelectorAll('.smallmenu-submenu-close');

let submenuFocusTraps = {};

function openSubmenu(toggle) {
    const submenu = document.querySelector(toggle.dataset.target);
    const closer = submenu.querySelector('.smallmenu-submenu-close');

    smallmenuSubmenus.forEach(otherSubmenu => {
        otherSubmenu.classList.remove('visible');
    });

    submenu.classList.add('visible');
    smallmenuInner.classList.add('submenu-is-open');

    toggle.setAttribute('aria-expanded', 'true');
    closer.setAttribute('aria-expanded', 'true');
    closer.focus();

    if (!submenuFocusTraps[submenu.id]) {
        const focusableEls = [closer, ...FocusTrap.getFocusableEls(submenu)];
        submenuFocusTraps[submenu.id] = new FocusTrap(submenu, focusableEls);
    }

    headerMenuFocusTrap.disable();
    submenuFocusTraps[submenu.id].enable();
}

function closeSubmenu(closer) {
    const submenu = closer.closest('.smallmenu-submenu');
    const toggle = document.querySelector(`[data-target="#${submenu.id}"]`);

    submenu.classList.remove('visible');
    smallmenuInner.classList.remove('submenu-is-open');
    smallmenuInner.classList.add('submenu-was-closed');

    closer.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-expanded', 'false');
    toggle.focus();

    submenuFocusTraps[submenu.id].disable();
    headerMenuFocusTrap.enable();
}

smallmenuSubmenuToggles.forEach(toggle => {
    toggle.addEventListener('click', () => {
        openSubmenu(toggle);
    });
});

smallMenuSubmenuClosers.forEach(closer => {
    closer.addEventListener('click', () => {
        closeSubmenu(closer);
    });
});


// Megamenus

const flyouts = document.querySelectorAll('.flyout');
const flyoutToggles = document.querySelectorAll('.flyout-toggle');
const flyoutClosers = document.querySelectorAll('.flyout-close');
const focusTraps = [];

function closeCurrentFlyout() {
    document.body.classList.remove('flyout-is-open');

    flyouts.forEach(menu => {
        menu.classList.remove('visible');
    });

    flyoutToggles.forEach(toggle => {
        toggle.setAttribute('aria-expanded', 'false');
        toggle.classList.remove('active');
    });

    focusTraps.forEach(trap => {
        trap.disable();
    });
}

flyoutToggles.forEach((toggle, index) => {
    const flyout = document.querySelector(toggle.dataset.target);
    
    if (!flyout) {
        return;
    }

    const closer = flyout.querySelector('.flyout-close');

    focusTraps[index] = new FocusTrap(flyout, [closer, ...FocusTrap.getFocusableEls(flyout)]);

    toggle.addEventListener('click', () => {
        const flyoutIsOpen = flyout.classList.contains('visible');

        closeCurrentFlyout();

        if (flyoutIsOpen) {
            return;
        }

        closeHeaderMenu();
        
        flyout.classList.add('visible');
        document.body.classList.add('flyout-is-open');
        toggle.classList.add('active');
        toggle.setAttribute('aria-expanded', 'true');
        closer.setAttribute('aria-expanded', 'true');
        closer.focus();
        focusTraps[index].enable();
    });
});

flyoutClosers.forEach((closer, index) => {
    closer.addEventListener('click', () => {
        const flyout = closer.closest('.flyout');
        const toggle = document.querySelector(`[data-target="#${flyout.id}"]`);

        closeCurrentFlyout();
        toggle.focus();
    });
});

window.addEventListener('keydown', e => {
    if (e.key === 'Escape') {
        closeCurrentFlyout();
    }
});


// FAQ blocks

const faqBlocks = document.querySelectorAll('.faq');

faqBlocks.forEach(block => {
    const itemToggles = block.querySelectorAll('.faq-item-toggle');

    function openItem(toggle) {
        const item = toggle.closest('.faq-item');
        const content = item.querySelector('.faq-item-content');
        const isClosed = !item.classList.contains('open');

        if (isClosed) {
            item.classList.add('open');
            content.style.maxHeight = `${content.scrollHeight}px`;
            reverseAriaExpanded(toggle);
        }
    }

    function closeItem(toggle) {
        const item = toggle.closest('.faq-item');
        const content = item.querySelector('.faq-item-content');
        const isOpen = item.classList.contains('open');

        if (isOpen) {
            item.classList.remove('open');
            content.style.maxHeight = null;
            reverseAriaExpanded(toggle);
        }
    }

    itemToggles.forEach(toggle => {
        toggle.addEventListener('click', () => {
            const itemWasOpen = toggle.closest('.faq-item').classList.contains('open');

            itemToggles.forEach(otherToggle => {
                closeItem(otherToggle);
            });

            itemWasOpen ? closeItem(toggle) : openItem(toggle);
        });
    });
});


// Cache-proofing dynamic weather

const dynamicWeatherContainers = document.querySelectorAll('.weather-dynamic');

if (dynamicWeatherContainers.length) {
    fetch(urls.ajax, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: 'action=get_weather',
    }).then(response => response.text()).then(html => {
        dynamicWeatherContainers.forEach(container => {
            container.innerHTML = html;
        });
    });
}


// Static map blocks

const staticmapBlocks = document.querySelectorAll('.staticmap');

staticmapBlocks.forEach(block => {
    const toggles = block.querySelectorAll('.staticmap-toggle');
    const hotspots = block.querySelectorAll('.staticmap-hotspot');

    toggles.forEach(toggle => {
        function showMarkerInfo(toggle) {
            const isMobile = window.matchMedia('(max-width: 60em)').matches;
            const target = block.querySelector(toggle.getAttribute('href'));

            toggles.forEach(t => t.classList.remove('active'));
            toggle.classList.add('active');

            hotspots.forEach(h => h.classList.remove('visible'));
            target.classList.add('visible');

            if (isMobile) {
                target.scrollIntoView({ behavior: 'smooth' });
            }
        }
        
        toggle.addEventListener('click', e => {
            e.preventDefault();
            showMarkerInfo(toggle);
        });
    });
});


// Routemaker links

const routemakerLinks = document.querySelectorAll('[href*="viewer.routemaker.nl"]');

routemakerLinks.forEach(link => {
    if (link.hasAttribute('download')) {
        return; // Skip links with download attribute
    }

    link.setAttribute('role', 'button');
    
    const dialog = document.createElement('dialog');
    dialog.classList.add('popup');

    const closeButton = document.createElement('button');
    closeButton.textContent = '×';
    closeButton.classList.add('popup-close');
    closeButton.setAttribute('type', 'button');
    closeButton.setAttribute('aria-label', 'Sluiten');
    closeButton.addEventListener('click', () => dialog.close());
    dialog.appendChild(closeButton);

    const iframe = document.createElement('iframe');
    iframe.classList.add('popup-iframe');
    iframe.setAttribute('src', link.href);
    iframe.setAttribute('allow', 'fullscreen');
    iframe.setAttribute('loading', 'lazy');
    iframe.setAttribute('sandbox', 'allow-same-origin allow-scripts allow-popups allow-forms');
    dialog.appendChild(iframe);

    document.body.appendChild(dialog);

    link.addEventListener('click', e => {
        e.preventDefault();
        dialog.showModal();
    });
});


// Fancybox

Fancybox.bind('[data-fancybox]');